export type BackupPlanQuant = {
  key: {
    sym: string;
    contract: string;
  };
  value: string;
};

export type BackupPlanOption = {
  id: number;
  display_id?: number;
  title: string;
  groupLabel: string;
  groupTitle: string;
  nodeCount: number;
  apply_balance: number;
  total_bbp_quota: number;
  applied_bbp_quota: number;
  fulfilled_bbp_quota: number;
  min_sum_quant: number;
  quants: BackupPlanQuant[];
  nfts: any[];
  started_at: string;
  ended_at: string;
  created_at: string;
  updated_at: string;
  text: string;
  disabledReason?: string;
};

export type BackupPlanGroup = {
  label: string;
  title: string;
  secondaryToken?: string;
  options: BackupPlanOption[];
};

const STARTED_AT = '2026-09-23T08:55:13';
const ENDED_AT = '2030-01-01T00:00:00';
const CREATED_AT = '2026-09-23T09:00:00';
const DEFAULT_BACKUP_PLAN_IDS = [1, 2, 5];
const LEGACY_PRODUCTION_PLAN_DISPLAY_IDS: Record<number, number> = {
  14: 1,
  15: 2,
  16: 3,
  17: 4,
  18: 5,
};

export const BACKUP_PLAN_IDS = (process.env.REACT_APP_BACKUP_PLAN_IDS || DEFAULT_BACKUP_PLAN_IDS.join(','))
  .split(',')
  .map(Number);

const AMAX = {
  sym: '8,AMAX',
  contract: 'amax.token',
};

const JU = {
  sym: '8,JU',
  contract: 'ju.token',
};

const formatTokenText = (quants: BackupPlanQuant[]) => {
  return quants
    .map((quant) => {
      const [amount, token] = quant.value.split(' ');
      return `${Number(amount).toLocaleString('en-US')} ${token}`;
    })
    .join(' + ');
};

const createPlan = ({
  id,
  groupLabel,
  groupTitle,
  nodeCount,
  quants,
  fulfilled = 0,
  disabledReason,
}: {
  id: number;
  groupLabel: string;
  groupTitle: string;
  nodeCount: number;
  quants: BackupPlanQuant[];
  fulfilled?: number;
  disabledReason?: string;
}): BackupPlanOption => {
  const min_sum_quant = quants.reduce((total, quant) => total + Number(quant.value.split(' ')[0]), 0);

  return {
    id,
    title: `${groupLabel} ${groupTitle}`,
    groupLabel,
    groupTitle,
    nodeCount,
    total_bbp_quota: nodeCount,
    applied_bbp_quota: fulfilled,
    fulfilled_bbp_quota: fulfilled,
    apply_balance: Math.max(nodeCount - fulfilled, 0),
    min_sum_quant,
    quants,
    nfts: [],
    started_at: STARTED_AT,
    ended_at: ENDED_AT,
    created_at: CREATED_AT,
    updated_at: '1970-01-01T00:00:00',
    text: formatTokenText(quants),
    disabledReason,
  };
};

export const BACKUP_PLAN_GROUPS: BackupPlanGroup[] = [
  {
    label: '方案一',
    title: 'AMAX + JU 资产包',
    secondaryToken: 'JU 资产包',
    options: [
      createPlan({
        id: BACKUP_PLAN_IDS[0],
        groupLabel: '方案一',
        groupTitle: 'AMAX + JU 资产包',
        nodeCount: 50,
        quants: [
          { key: AMAX, value: '800.00000000 AMAX' },
          { key: JU, value: '10000.00000000 JU' },
        ],
      }),
      createPlan({
        id: BACKUP_PLAN_IDS[1],
        groupLabel: '方案一',
        groupTitle: 'AMAX + JU 资产包',
        nodeCount: 100,
        quants: [
          { key: AMAX, value: '1000.00000000 AMAX' },
          { key: JU, value: '5000.00000000 JU' },
        ],
      }),
    ],
  },
  {
    label: '方案三',
    title: 'AMAX 纯资产认购',
    options: [
      createPlan({
        id: BACKUP_PLAN_IDS[2],
        groupLabel: '方案三',
        groupTitle: 'AMAX 纯资产认购',
        nodeCount: 350,
        quants: [{ key: AMAX, value: '1200.00000000 AMAX' }],
      }),
    ],
  },
];

export const BACKUP_PLAN_OPTIONS = BACKUP_PLAN_GROUPS
  .flatMap((group) => group.options)
  .map((option, index) => ({ ...option, display_id: index + 1 }));

export const getBackupPlanDisplayId = (planId: number | string) => {
  const numericPlanId = Number(planId);
  return BACKUP_PLAN_OPTIONS.find((option) => option.id === numericPlanId)?.display_id
    ?? LEGACY_PRODUCTION_PLAN_DISPLAY_IDS[numericPlanId]
    ?? planId;
};
